import React, { useEffect, useMemo, useRef, useState } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

const DemographicSelector = ({
  demographicData,
  onSelectionChange,
  primaryColor = '#03aae4',
  value = {},
  hasError = false,
}) => {
  const MAX_VISIBLE_LEVELS = 5;
  const [selections, setSelections] = useState(value || {});
  const [expandedNodes, setExpandedNodes] = useState({});
  const [userInteracted, setUserInteracted] = useState({});
  const onSelectionChangeRef = useRef(onSelectionChange);
  const prevValueRef = useRef(value);

  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  const getKey = (demographicName, levelIndex) =>
    `${demographicName}_level_${levelIndex}`;

  const isMultipleDemographic = (demographic) =>
    demographic.allowMultipleSelection === true;

  const asArray = (selectedValue) => {
    if (Array.isArray(selectedValue)) return selectedValue;
    return selectedValue ? [selectedValue] : [];
  };

  const getMaxDepth = (nodes) => {
    if (!nodes || nodes.length === 0) return 0;

    let maxDepth = 0;

    const traverse = (list, depth) => {
      list.forEach((node) => {
        maxDepth = Math.max(maxDepth, depth);
        if (node.children && node.children.length > 0) {
          traverse(node.children, depth + 1);
        }
      });
    };

    traverse(nodes, 0);
    return Math.min(maxDepth + 1, MAX_VISIBLE_LEVELS);
  };

  const findNode = (nodes, id) => {
    for (const node of nodes || []) {
      if (node.id === id) return node;
      if (node.children && node.children.length > 0) {
        const found = findNode(node.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const collectDescendantIds = (node) => {
    const ids = [];

    const traverse = (current) => {
      (current.children || []).forEach((child) => {
        ids.push(child.id);
        traverse(child);
      });
    };

    if (node) traverse(node);
    return ids;
  };

  const findNodeDepth = (nodes, id, depth = 0) => {
    for (const node of nodes || []) {
      if (node.id === id) return depth;
      const childDepth = findNodeDepth(node.children, id, depth + 1);
      if (childDepth !== -1) return childDepth;
    }
    return -1;
  };

  // Ruta completa (ancestros incluidos) desde la raíz hasta el nodo con ese id.
  const findNodePath = (nodes, id, path = []) => {
    for (const node of nodes || []) {
      const nextPath = [...path, node];
      if (node.id === id) return nextPath;
      if (node.children && node.children.length > 0) {
        const found = findNodePath(node.children, id, nextPath);
        if (found) return found;
      }
    }
    return null;
  };

  const getOptionsForLevel = (demographic, levelIndex, currentSelections) => {
    if (levelIndex === 0) return demographic.nodes || [];

    const previousKey = getKey(demographic.demographicName, levelIndex - 1);
    const previousLevelSelection = currentSelections[previousKey];
    if (!previousLevelSelection) return [];

    const parentNode = findNode(demographic.nodes, previousLevelSelection);
    return parentNode?.children || [];
  };

  const deleteFollowingLevels = (
    target,
    demographicName,
    startLevel,
    maxLevels
  ) => {
    for (let i = startLevel; i < maxLevels; i++) {
      delete target[getKey(demographicName, i)];
    }
  };

  const cleanSingleSelection = (demographic, currentSelections) => {
    const cleaned = currentSelections;
    const maxLevels = getMaxDepth(demographic.nodes);

    for (let level = 0; level < maxLevels; level++) {
      const key = getKey(demographic.demographicName, level);
      const selectedValue = cleaned[key];

      if (!selectedValue) {
        deleteFollowingLevels(
          cleaned,
          demographic.demographicName,
          level + 1,
          maxLevels
        );
        break;
      }

      const options = getOptionsForLevel(demographic, level, cleaned);
      const selectedOption = options.find((option) => option.id === selectedValue);

      if (!selectedOption) {
        delete cleaned[key];
        deleteFollowingLevels(
          cleaned,
          demographic.demographicName,
          level + 1,
          maxLevels
        );
        break;
      }

      if (!selectedOption.children || selectedOption.children.length === 0) {
        deleteFollowingLevels(
          cleaned,
          demographic.demographicName,
          level + 1,
          maxLevels
        );
        break;
      }
    }
  };

  const cleanMultipleSelection = (demographic, currentSelections) => {
    const cleaned = currentSelections;
    const maxLevels = getMaxDepth(demographic.nodes);
    const selectedByLevel = {};

    for (let level = 0; level < maxLevels; level++) {
      const key = getKey(demographic.demographicName, level);
      const validIds = asArray(cleaned[key]).filter((id) => {
        const node = findNode(demographic.nodes, id);
        return node && findNodeDepth(demographic.nodes, id) === level;
      });

      if (validIds.length > 0) {
        selectedByLevel[level] = validIds;
        cleaned[key] = validIds;
      } else {
        delete cleaned[key];
      }
    }

    for (let level = 1; level < maxLevels; level++) {
      const key = getKey(demographic.demographicName, level);
      if (!cleaned[key]) continue;

      const validParentIds = Object.values(selectedByLevel)
        .flat()
        .flatMap((id) => [id, ...collectDescendantIds(findNode(demographic.nodes, id))]);

      const validIds = cleaned[key].filter((id) => validParentIds.includes(id));
      if (validIds.length > 0) {
        cleaned[key] = validIds;
      } else {
        delete cleaned[key];
      }
    }

    for (let level = MAX_VISIBLE_LEVELS; level < 20; level++) {
      delete cleaned[getKey(demographic.demographicName, level)];
    }
  };

  const cleanSelections = (currentSelections) => {
    const cleaned = { ...(currentSelections || {}) };

    demographicData.forEach((demographic) => {
      if (isMultipleDemographic(demographic)) {
        cleanMultipleSelection(demographic, cleaned);
      } else {
        cleanSingleSelection(demographic, cleaned);
      }
    });

    return cleaned;
  };

  useEffect(() => {
    if (JSON.stringify(prevValueRef.current) !== JSON.stringify(value)) {
      prevValueRef.current = value;
      setSelections(cleanSelections(value || {}));
    }
  }, [value]);

  useEffect(() => {
    const cleanedSelections = cleanSelections(selections);
    if (JSON.stringify(cleanedSelections) !== JSON.stringify(selections)) {
      setSelections(cleanedSelections);
      return;
    }

    if (onSelectionChangeRef.current) {
      onSelectionChangeRef.current(cleanedSelections);
    }
  }, [selections]);

  const shouldShowLevel = (demographic, levelIndex, currentSelections) => {
    if (levelIndex === 0) return true;

    const prevKey = getKey(demographic.demographicName, levelIndex - 1);
    const prevId = currentSelections[prevKey];
    if (!prevId) return false;

    const options = getOptionsForLevel(
      demographic,
      levelIndex,
      currentSelections
    );
    return options.length > 0;
  };

  const areAllVisibleLevelsComplete = (demographic) => {
    if (isMultipleDemographic(demographic)) {
      return asArray(selections[getKey(demographic.demographicName, 0)]).length > 0;
    }

    const maxLevels = getMaxDepth(demographic.nodes);

    for (let level = 0; level < maxLevels; level++) {
      const key = getKey(demographic.demographicName, level);
      const options = getOptionsForLevel(demographic, level, selections);
      const isVisible = shouldShowLevel(demographic, level, selections);
      const hasSelection = Boolean(selections[key]);

      if (isVisible && options.length > 0 && !hasSelection) return false;
      if (!isVisible) break;
    }

    return true;
  };

  const handleSingleLevelChange = (demographicName, levelIndex, selectedValue) => {
    const newSelections = { ...selections };
    const key = getKey(demographicName, levelIndex);

    // For single selection, never store arrays - always a singular value
    newSelections[key] = selectedValue;
    setUserInteracted((prev) => ({ ...prev, [key]: true }));

    const demographic = demographicData.find(
      (d) => d.demographicName === demographicName
    );

    if (demographic) {
      const maxLevels = getMaxDepth(demographic.nodes);
      deleteFollowingLevels(
        newSelections,
        demographicName,
        levelIndex + 1,
        maxLevels
      );
      setUserInteracted((prev) => {
        const updated = { ...prev };
        deleteFollowingLevels(updated, demographicName, levelIndex + 1, maxLevels);
        return updated;
      });
    }

    setSelections(cleanSelections(newSelections));
  };

  const handleMultipleToggle = (demographic, node, checked) => {
    const levelIndex = findNodeDepth(demographic.nodes, node.id);
    if (levelIndex < 0 || levelIndex >= MAX_VISIBLE_LEVELS) return;

    const key = getKey(demographic.demographicName, levelIndex);
    const newSelections = { ...selections };

    setUserInteracted((prev) => ({ ...prev, [key]: true }));

    if (checked) {
      // Al marcar un nodo hijo, seleccionamos también toda su cadena de
      // ancestros para que los niveles superiores queden marcados y se guarden.
      const path = findNodePath(demographic.nodes, node.id) || [node];
      const touchedKeys = [];

      path.forEach((pathNode) => {
        const pathLevel = findNodeDepth(demographic.nodes, pathNode.id);
        if (pathLevel < 0 || pathLevel >= MAX_VISIBLE_LEVELS) return;

        const levelKey = getKey(demographic.demographicName, pathLevel);
        const existing = asArray(newSelections[levelKey]);
        newSelections[levelKey] = [...new Set([...existing, pathNode.id])];
        touchedKeys.push(levelKey);
      });

      setUserInteracted((prev) => {
        const updated = { ...prev };
        touchedKeys.forEach((levelKey) => {
          updated[levelKey] = true;
        });
        return updated;
      });
    } else {
      const descendantIds = collectDescendantIds(node);
      const maxLevels = getMaxDepth(demographic.nodes);

      for (let level = levelIndex; level < maxLevels; level++) {
        const levelKey = getKey(demographic.demographicName, level);
        const idsToRemove = level === levelIndex ? [node.id] : descendantIds;
        const remaining = asArray(newSelections[levelKey]).filter(
          (id) => !idsToRemove.includes(id)
        );

        if (remaining.length > 0) {
          newSelections[levelKey] = remaining;
        } else {
          delete newSelections[levelKey];
        }
      }

      // Cascada hacia arriba: si un ancestro se queda sin descendientes
      // seleccionados, también se desmarca (hasta la raíz).
      const selectedIds = new Set(
        Array.from({ length: maxLevels }).flatMap((_, level) =>
          asArray(newSelections[getKey(demographic.demographicName, level)])
        )
      );
      // De abajo hacia arriba (padre → raíz) para que la cascada propague.
      const ancestors = (findNodePath(demographic.nodes, node.id) || [])
        .slice(0, -1)
        .reverse();

      ancestors.forEach((ancestor) => {
        const ancestorLevel = findNodeDepth(demographic.nodes, ancestor.id);
        if (ancestorLevel < 0 || ancestorLevel >= MAX_VISIBLE_LEVELS) return;

        const hasSelectedDescendant = collectDescendantIds(ancestor).some((id) =>
          selectedIds.has(id)
        );
        if (hasSelectedDescendant) return;

        const levelKey = getKey(demographic.demographicName, ancestorLevel);
        const remaining = asArray(newSelections[levelKey]).filter(
          (id) => id !== ancestor.id
        );

        if (remaining.length > 0) {
          newSelections[levelKey] = remaining;
        } else {
          delete newSelections[levelKey];
        }
        selectedIds.delete(ancestor.id);
      });
    }

    setSelections(cleanSelections(newSelections));
  };

  const toggleExpanded = (nodeId) => {
    setExpandedNodes((prev) => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };

  const expandAll = (nodes) => {
    const nextExpanded = {};

    const traverse = (list) => {
      list.forEach((node) => {
        if (node.children?.length > 0) {
          nextExpanded[node.id] = true;
          traverse(node.children);
        }
      });
    };

    traverse(nodes);
    setExpandedNodes((prev) => ({ ...prev, ...nextExpanded }));
  };

  const collapseAll = (nodes) => {
    const ids = [];

    const traverse = (list) => {
      list.forEach((node) => {
        if (node.children?.length > 0) {
          ids.push(node.id);
          traverse(node.children);
        }
      });
    };

    traverse(nodes);
    setExpandedNodes((prev) => {
      const next = { ...prev };
      ids.forEach((id) => delete next[id]);
      return next;
    });
  };

  const clearMultipleSelection = (demographic) => {
    const maxLevels = getMaxDepth(demographic.nodes);
    const newSelections = { ...selections };

    for (let level = 0; level < maxLevels; level++) {
      delete newSelections[getKey(demographic.demographicName, level)];
    }

    setSelections(cleanSelections(newSelections));
  };

  const getMultipleSelectedCount = (demographic) => {
    const maxLevels = getMaxDepth(demographic.nodes);
    let count = 0;

    for (let level = 0; level < maxLevels; level++) {
      count += asArray(selections[getKey(demographic.demographicName, level)])
        .length;
    }

    return count;
  };

  const renderMultipleTreeNode = (demographic, node, levelIndex) => {
    if (levelIndex >= MAX_VISIBLE_LEVELS) return null;

    const key = getKey(demographic.demographicName, levelIndex);
    const selectedIds = asArray(selections[key]);
    const isChecked = selectedIds.includes(node.id);
    const hasChildren = node.children?.length > 0;
    const isExpanded = expandedNodes[node.id];
    const hasMultipleLevels = demographic.nodes?.some(
      (n) => n.children?.length > 0
    );
    const levelName =
      demographic.levelNames?.[levelIndex] ?? `Nivel ${levelIndex + 1}`;

    return (
      <Box key={node.id} sx={{ ml: levelIndex === 0 ? 0 : 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            py: 0.75,
            px: 1,
            borderRadius: 1.25,
            '&:hover': { bgcolor: 'rgba(3, 170, 228, 0.06)' },
          }}
        >
          <IconButton
            size="small"
            onClick={() => hasChildren && toggleExpanded(node.id)}
            disabled={!hasChildren}
            sx={{
              width: 26,
              height: 26,
              color: hasChildren ? primaryColor : 'transparent',
            }}
          >
            {hasChildren ? (
              isExpanded ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )
            ) : hasMultipleLevels ? (
              <span style={{ fontSize: '1.4rem', fontWeight: 'bold', lineHeight: '1', color: primaryColor }}>−</span>
            ) : null}
          </IconButton>

          <Checkbox
            size="small"
            checked={isChecked}
            onChange={(event) =>
              handleMultipleToggle(demographic, node, event.target.checked)
            }
            sx={{
              p: 0.25,
              color: primaryColor,
              '&.Mui-checked': { color: primaryColor },
            }}
          />

          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, flex: 1 }}>
            {node.value}
          </Typography>

          <Chip
            size="small"
            variant="outlined"
            label={levelName}
            sx={{
              height: 22,
              fontSize: '0.68rem',
              color: 'text.secondary',
              borderColor: 'divider',
            }}
          />
        </Stack>

        {hasChildren && levelIndex + 1 < MAX_VISIBLE_LEVELS && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box
              sx={{
                ml: 2.25,
                pl: 1.5,
                borderLeft: '2px solid',
                borderColor: 'divider',
              }}
            >
              {node.children.map((child) =>
                renderMultipleTreeNode(
                  demographic,
                  child,
                  levelIndex + 1
                )
              )}
            </Box>
          </Collapse>
        )}
      </Box>
    );
  };

  const renderMultipleTreeDemographic = (demographic, allLevelsComplete) => {
    const rootKey = getKey(demographic.demographicName, 0);
    const selectedCount = getMultipleSelectedCount(demographic);
    const fieldHasError =
      hasError &&
      selectedCount === 0 &&
      !allLevelsComplete &&
      !userInteracted[rootKey];

    return (
      <Box>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 1.5 }}
        >
          <Chip
            clickable
            size="small"
            label="Expandir"
            onClick={() => expandAll(demographic.nodes)}
          />
          <Chip
            clickable
            size="small"
            label="Contraer"
            onClick={() => collapseAll(demographic.nodes)}
          />
          <Chip
            clickable
            size="small"
            label="Limpiar"
            onClick={() => clearMultipleSelection(demographic)}
          />
        </Stack>

        <Box
          sx={{
            border: '1px solid',
            borderColor: fieldHasError ? 'error.main' : 'divider',
            borderRadius: 2,
            p: 1,
            bgcolor: 'background.paper',
          }}
        >
          {demographic.nodes.map((node) =>
            renderMultipleTreeNode(demographic, node, 0)
          )}
        </Box>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 1 }}
        >
          <Chip
            size="small"
            variant="outlined"
            label={`${selectedCount} seleccionado(s)`}
            sx={{ color: primaryColor, borderColor: primaryColor }}
          />
        </Stack>

        {fieldHasError && (
          <Typography
            variant="caption"
            color="error"
            sx={{
              marginTop: '0.25rem',
              fontWeight: 500,
              fontSize: '0.75rem',
              display: 'block',
            }}
          >
            Selecciona al menos un elemento
          </Typography>
        )}
      </Box>
    );
  };

  if (!demographicData || demographicData.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        No hay datos demográficos disponibles
      </Typography>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {demographicData.map((demographic, demoIndex) => {
        if (!demographic.nodes || demographic.nodes.length === 0) return null;

        const maxLevels = getMaxDepth(demographic.nodes);
        const allLevelsComplete = areAllVisibleLevelsComplete(demographic);
        const allowMultipleSelection = isMultipleDemographic(demographic);

        return (
          <Box key={demographic.demographicName} sx={{ marginBottom: '2rem' }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ marginBottom: '1rem' }}
              flexWrap="wrap"
              useFlexGap
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  color: primaryColor,
                }}
              >
                {demographic.demographicName}
              </Typography>
              {allowMultipleSelection && (
                <Chip
                  size="small"
                  variant="outlined"
                  label="Selección múltiple"
                  sx={{ color: primaryColor, borderColor: primaryColor }}
                />
              )}
            </Stack>

            {allowMultipleSelection
              ? renderMultipleTreeDemographic(demographic, allLevelsComplete)
              : Array.from({ length: maxLevels }).map((_, levelIndex) => {
                  const key = getKey(demographic.demographicName, levelIndex);
                  const levelName =
                    demographic.levelNames?.[levelIndex] ??
                    `Nivel ${levelIndex + 1}`;
                  const options = getOptionsForLevel(
                    demographic,
                    levelIndex,
                    selections
                  );
                  const selectedValue = selections[key];
                  const isVisible = shouldShowLevel(
                    demographic,
                    levelIndex,
                    selections
                  );
                  const fieldHasError =
                    hasError &&
                    isVisible &&
                    options.length > 0 &&
                    !selectedValue &&
                    !allLevelsComplete &&
                    !userInteracted[key];

                  if (!isVisible) return null;

                  return (
                    <FormControl
                      key={key}
                      fullWidth
                      sx={{ marginBottom: '1.5rem' }}
                      error={fieldHasError}
                    >
                      <Select
                        value={selectedValue || ''}
                        onChange={(event) =>
                          handleSingleLevelChange(
                            demographic.demographicName,
                            levelIndex,
                            event.target.value
                          )
                        }
                        displayEmpty
                        disabled={options.length === 0}
                        error={fieldHasError}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: fieldHasError
                              ? '#f44336 !important'
                              : 'rgba(0, 0, 0, 0.23)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: fieldHasError
                              ? '#f44336 !important'
                              : 'rgba(0, 0, 0, 0.87)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: fieldHasError
                              ? '#f44336 !important'
                              : primaryColor,
                          },
                        }}
                      >
                        <MenuItem value="" disabled>
                          <em>Selecciona {levelName.toLowerCase()}</em>
                        </MenuItem>
                        {options.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.value}
                          </MenuItem>
                        ))}
                      </Select>

                      {fieldHasError && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{
                            marginTop: '0.25rem',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            display: 'block',
                          }}
                        >
                          Este campo es requerido
                        </Typography>
                      )}
                    </FormControl>
                  );
                })}

            {demoIndex < demographicData.length - 1 && (
              <Divider sx={{ marginTop: '2rem' }} />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default DemographicSelector;
